import React, { useEffect, useMemo, useState } from 'react';
import {
    Paper,
    Typography,
    Grid,
    Box,
    Stack,
    Button,
    Divider,
    FormControl,
    FormLabel,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Alert,
    useTheme,
} from '@mui/material';
import { Save, RestartAlt } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import FormInput from '../inputs/FormInput';
import SwitchInput from '../inputs/SwitchInput';
import { WEBHOOK_AVAILABLE_EVENTS } from './webhookConstants';

const buildInitial = (webhook) => ({
    name: webhook?.name || '',
    url: webhook?.url || '',
    events: Array.isArray(webhook?.events) ? webhook.events : [],
    is_active: webhook?.is_active !== false,
});

const FormAddEditWebhook = ({
    mode = 'create',
    webhook,
    onSubmit,
    isSubmitting,
    extraSlot,
}) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const theme = useTheme();

    const isEdit = mode === 'edit';
    const initial = useMemo(() => buildInitial(webhook), [webhook]);
    const [form, setForm] = useState(initial);
    const [touched, setTouched] = useState(false);

    useEffect(() => {
        setForm(initial);
        setTouched(false);
    }, [initial]);

    const setField = (field) => (eOrValue) => {
        const value = eOrValue?.target ? eOrValue.target.value : eOrValue;
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const toggleEvent = (eventValue) => {
        setForm((prev) => {
            const has = prev.events.includes(eventValue);
            return {
                ...prev,
                events: has
                    ? prev.events.filter((e) => e !== eventValue)
                    : [...prev.events, eventValue],
            };
        });
    };

    const isHttp = /^http:\/\//i.test((form.url || '').trim());
    const eventsValid = form.events.length > 0;
    const canSubmit = form.name.trim() && form.url.trim() && eventsValid;

    const handleSubmit = (e) => {
        e.preventDefault();
        setTouched(true);
        if (!canSubmit) return;
        onSubmit?.({
            name: form.name.trim(),
            url: form.url.trim(),
            events: form.events,
            is_active: Boolean(form.is_active),
        });
    };

    const handleReset = () => setForm(initial);

    return (
        <Paper
            component="form"
            onSubmit={handleSubmit}
            elevation={0}
            sx={{
                p: { xs: 2, md: 3 },
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
            }}
        >
            <Box mb={2}>
                <Typography variant="h6" fontWeight={700}>
                    {isEdit
                        ? (isArabic ? 'تعديل Webhook' : 'Edit Webhook')
                        : (isArabic ? 'إضافة Webhook' : 'New Webhook')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {isArabic
                        ? 'سيتم إرسال طلب POST موقّع بـ HMAC إلى الرابط أدناه عند وقوع أي من الأحداث المختارة.'
                        : 'A signed (HMAC) POST request will be sent to the URL below for every selected event.'}
                </Typography>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <FormInput
                        type="text"
                        name="name"
                        label={isArabic ? 'الاسم' : 'Name'}
                        placeholder={isArabic ? 'مثال: Slack notifications' : 'e.g. Slack notifications'}
                        value={form.name}
                        onChange={setField('name')}
                        required
                        error={touched && !form.name.trim()
                            ? (isArabic ? 'الاسم مطلوب' : 'Name is required')
                            : ''}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <FormInput
                        type="text"
                        name="url"
                        label={isArabic ? 'الرابط (URL)' : 'URL'}
                        placeholder="https://example.com/webhooks/shop"
                        value={form.url}
                        onChange={setField('url')}
                        required
                        error={touched && !form.url.trim()
                            ? (isArabic ? 'الرابط مطلوب' : 'URL is required')
                            : ''}
                    />
                    {isHttp && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                            {isArabic
                                ? 'الرابط يستخدم http بدلاً من https — استخدم https في الإنتاج.'
                                : 'URL uses http instead of https — use https in production.'}
                        </Alert>
                    )}
                </Grid>

                <Grid item xs={12}>
                    <FormControl
                        component="fieldset"
                        error={touched && !eventsValid}
                        sx={{ width: '100%' }}
                    >
                        <FormLabel
                            component="legend"
                            sx={{
                                mb: 1,
                                fontWeight: 600,
                                color: 'text.primary',
                            }}
                        >
                            {isArabic ? 'الأحداث' : 'Events'}
                        </FormLabel>
                        <FormGroup>
                            <Grid container spacing={1}>
                                {WEBHOOK_AVAILABLE_EVENTS.map((evt) => (
                                    <Grid item xs={12} sm={6} md={4} key={evt.value}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={form.events.includes(evt.value)}
                                                    onChange={() => toggleEvent(evt.value)}
                                                />
                                            }
                                            label={
                                                <Box>
                                                    <Typography variant="body2" fontWeight={600}>
                                                        {isArabic ? evt.labelAr : evt.labelEn}
                                                    </Typography>
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
                                                    >
                                                        {evt.value}
                                                    </Typography>
                                                </Box>
                                            }
                                            sx={{ alignItems: 'flex-start' }}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </FormGroup>
                        {touched && !eventsValid && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                {isArabic ? 'اختر حدثًا واحدًا على الأقل' : 'Pick at least one event'}
                            </Typography>
                        )}
                    </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                    <SwitchInput
                        label={isArabic ? 'مُفعّل' : 'Active'}
                        name="is_active"
                        checked={Boolean(form.is_active)}
                        onChange={(e) => setForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                        helperText={isArabic
                            ? 'عند إيقافه، لا يستقبل أي أحداث جديدة.'
                            : 'When off, no new events are delivered.'}
                    />
                </Grid>
            </Grid>

            {extraSlot}

            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1.5}
                sx={{ mt: 3 }}
                justifyContent="flex-end"
            >
                <Button
                    type="button"
                    variant="outlined"
                    color="inherit"
                    startIcon={<RestartAlt />}
                    onClick={handleReset}
                    disabled={isSubmitting}
                >
                    {isArabic ? 'استعادة' : 'Reset'}
                </Button>
                <Button
                    type="submit"
                    variant="contained"
                    startIcon={<Save />}
                    disabled={isSubmitting || !canSubmit}
                >
                    {isSubmitting
                        ? (isArabic ? 'جاري الحفظ...' : 'Saving...')
                        : isEdit
                            ? (isArabic ? 'حفظ التغييرات' : 'Save changes')
                            : (isArabic ? 'إنشاء' : 'Create')}
                </Button>
            </Stack>
        </Paper>
    );
};

export default FormAddEditWebhook;
